import { useCallback, useMemo, useState } from 'react';
import { ClipboardList } from 'lucide-react';
import { DataTable } from '@/components/common/DataTable';
import { ErrorState } from '@/components/common/ErrorState';
import { useInfiniteTableQuery } from '@/hooks/useInfiniteTableQuery';
import { surveySubmissionsInfiniteQueryOptions } from '../api/surveys.api';
import type { SurveyRespondent, SurveySubmissionListParams } from '../types/survey.types';
import { getSurveyRespondentColumns } from './SurveyRespondentColumns';
import { SurveyRespondentDetailModal } from './SurveyRespondentDetailModal';

interface SurveySubmissionListTableProps {
  surveyId: number;
  listQueryParams: Omit<SurveySubmissionListParams, 'page'>;
}

export function SurveySubmissionListTable({
  surveyId,
  listQueryParams,
}: SurveySubmissionListTableProps) {
  const {
    rows: respondents,
    isLoading,
    loadError,
    refetch,
    loadMoreProps,
  } = useInfiniteTableQuery(surveySubmissionsInfiniteQueryOptions(surveyId, listQueryParams));

  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const openRespondent = useCallback(
    (respondent: SurveyRespondent) => setSelectedUserId(respondent.userId),
    [],
  );
  const columns = useMemo(() => getSurveyRespondentColumns(openRespondent), [openRespondent]);
  const selectedRespondent = useMemo(
    () => respondents.find((r) => r.userId === selectedUserId) ?? null,
    [respondents, selectedUserId],
  );

  if (loadError) {
    return (
      <ErrorState
        error={loadError}
        fallbackTitle="Could not load submissions"
        onRetry={() => void refetch()}
        fill
      />
    );
  }

  return (
    <>
      <DataTable
        data={respondents}
        columns={columns}
        isLoading={isLoading}
        stickyColumnId="userName"
        emptyIcon={ClipboardList}
        {...loadMoreProps}
        onRowClick={openRespondent}
        className="lg:flex-1"
      />

      <SurveyRespondentDetailModal
        respondent={selectedRespondent}
        open={selectedRespondent !== null}
        onOpenChange={(open) => {
          if (!open) setSelectedUserId(null);
        }}
      />
    </>
  );
}
